import { type MouseEvent, type ReactNode } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { HealedHeartMark } from '@/components/ui/healed-heart-mark';
import { useMemberDisplay } from '@/hooks/use-member-display';
import { cn } from '@/utils/cn';
import type { TeamMember } from '../types';
import { PhotoLayer } from './team-photo';
import { TopChips } from './team-card-chips';

function EditBtn(p: { onClick: () => void; hasPhrase: boolean }): ReactNode {
  const Icon = p.hasPhrase ? Pencil : Plus;
  const label = p.hasPhrase ? 'Editar frase' : 'Añadir frase';
  const handle = (e: MouseEvent): void => {
    e.stopPropagation();
    p.onClick();
  };
  return (
    <button
      onClick={handle}
      className="mt-xs flex w-full items-center justify-center gap-xs rounded-md bg-brand-red px-sm py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-coral"
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

function igUrl(m: TeamMember): string | undefined {
  if (!m.photo) return undefined;
  return `/api/files/team_members/${m.id}/${m.photo}`;
}

function NameLine({ member }: { member: TeamMember }): ReactNode {
  const display = useMemberDisplay(member);
  return (
    <div className="flex items-center gap-sm">
      <Avatar src={igUrl(member)} name={member.first_name} size="sm" />
      <span className="truncate text-sm font-semibold text-white">
        {display.fullName ?? display.name}
      </span>
      {display.redemptionScar && <HealedHeartMark />}
      {!!member.player_number && (
        <span className="shrink-0 text-[11px] font-medium text-white/60">
          #{member.player_number}
        </span>
      )}
      {member.top_tier && (
        <span className="text-[11px] text-warning">&#11088;</span>
      )}
    </div>
  );
}

function GlassInfo(p: {
  member: TeamMember;
  isSelf: boolean;
  onEdit: () => void;
}): ReactNode {
  return (
    <div className="absolute inset-x-0 bottom-0 bg-black/25 px-sm pb-xs pt-sm text-left backdrop-blur-sm">
      {/* COUPLED: keep paddings/margins in sync with crop-overlay.tsx GLASS_CLS/PHRASE_CLS/EDIT_CLS */}
      <NameLine member={p.member} />
      {!!p.member.personal_phrase && (
        <p className="mt-xs line-clamp-2 text-xs italic leading-snug text-white/80">
          {p.member.personal_phrase}
        </p>
      )}
      {p.isSelf && (
        <EditBtn onClick={p.onEdit} hasPhrase={!!p.member.personal_phrase} />
      )}
    </div>
  );
}

export function TeamCardPhoto(p: {
  member: TeamMember;
  isSelf: boolean;
  onEdit: () => void;
}): ReactNode {
  return (
    <div
      className={cn(
        'relative aspect-[9/16] w-full overflow-hidden rounded-lg',
        p.isSelf && 'ring-2 ring-brand-red'
      )}
    >
      <PhotoLayer member={p.member} />
      <TopChips member={p.member} />
      <GlassInfo member={p.member} isSelf={p.isSelf} onEdit={p.onEdit} />
    </div>
  );
}
